import { useMemo } from 'react';
import { Button, Card } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ButtonSplitGroup } from '@components/Buttons';
import {
  BootstrapGenericTable,
  useTableRowsSelection,
} from '@components/Table';
import { SelectionColumn } from '@components/Table/Columns';
import { cartApi } from '@reducers/order';
import { hidePlanDetails, showPlanDetails } from '@reducers/plan/plan';

const renderActionCell = ({ row }) => {
  const dispatch = useDispatch();

  const { planInfo } = useSelector((state) => state.plan);
  const { currentPlan, details } = planInfo.showState;

  const plan = {
    id: row.original.pstn_plan.id,
    type: row.original.pstn_plan.type,
    name: row.original.pstn_plan.name,
    outboundRates: true,
  };
  const isCurrent =
    currentPlan.id === plan.id && currentPlan.type === plan.type;
  const isDetailsActive = isCurrent && details;
  const onClick = () => {
    if (!isCurrent) {
      dispatch(hidePlanDetails());
    }
    if (isDetailsActive) {
      dispatch(hidePlanDetails());
      return;
    }
    dispatch(showPlanDetails(plan));
  };
  return (
    <Button size="sm" variant="didxl" onClick={onClick}>
      Info
    </Button>
  );
};

const exportContent = (selectedRows, clearAllRowsSelection, tableInstance) => {
  const navigate = useNavigate();
  const [trigger, { isLoading }] = cartApi.useAddPSTNUpgradesToCartMutation();
  const { login } = useSelector((state) => state.auth.user) || '';
  const onClick = () => {
    const selectedDids = selectedRows.map((item) => item.id);
    const plan = selectedRows.map((item) => item.pstn_plan)[0];
    trigger({ dids: selectedDids, customer: login })
      .unwrap()
      .then(() => {
        navigate(
          `/order/pstn-upgrade/plans/${plan.id}/type/${plan.type}/checkout`
        );
      });
  };
  return (
    <ButtonSplitGroup>
      <Button size="sm" variant="didxl" onClick={onClick} disabled={isLoading}>
        Upgrade
      </Button>
      <Button
        size="sm"
        variant="didxl"
        onClick={clearAllRowsSelection(tableInstance)}
      >
        Cancel
      </Button>
    </ButtonSplitGroup>
  );
};

const emptyTableContent = () => (
  <span className="textRed normalFontSize fw-bold">
    To check available DIDs to PSTN Upgrade, please select country and dedicated
    plan.
  </span>
);

export const PSTNUpgradeTable = ({ useData, useDataArgs }) => {
  const {
    selectedRows,
    clearAllRowsSelection,
    onRowSelectionChange,
    onAllRowsSelectionChange,
  } = useTableRowsSelection();
  const [searchParams] = useSearchParams();
  const country = searchParams.get('country') || '';
  const plan = searchParams.get('plan');
  const filterData = useMemo(() => ({}), [country, plan]);
  const columns = useMemo(
    () => [
      {
        id: 'did',
        Header: 'Number',
        accessor: 'did',
        className: 'col-md-3',
      },
      {
        id: 'area',
        Header: 'City/Area Code',
        accessor: 'area',
        className: 'col-md-3',
      },
      {
        id: 'type-of-service',
        Header: 'TOS',
        accessor: 'type_of_service',
        className: 'col-md-2',
      },
      {
        id: 'plan',
        Header: 'Plan',
        accessor: 'plan',
        className: 'col-md-2 text-center',
      },
      {
        Header: 'Action',
        Cell: renderActionCell,
        accessor: 'action',
        className: 'col-md-1 text-center',
      },
      SelectionColumn({
        className: 'col-md-1 text-center',
        controlled: true,
        onRowSelectionChange,
        onAllRowsSelectionChange,
      }),
    ],
    []
  );
  return (
    <Card>
      <Card.Header className="bg-didxl text-white">Upgrade to PSTN</Card.Header>
      <Card.Body>
        <BootstrapGenericTable
          columns={columns}
          useData={useData}
          useDataArgs={useDataArgs}
          filterData={filterData}
          tableProps={() => ({
            responsive: true,
            bordered: true,
            className: 'normalFontSize',
          })}
          paginated
          exportContent={(tableInstance) =>
            exportContent(selectedRows, clearAllRowsSelection, tableInstance)
          }
          selectedRows
          emptyMessageContent={emptyTableContent}
        />
      </Card.Body>
    </Card>
  );
};
